import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { SUPER_ADMIN_EMAIL } from '../config/appConfig';
import { trackSignUp, trackLogin, setUserId, trackEvent } from '../Utils/analytics';
import './SubscribeButton.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === 'true' && TURNSTILE_SITE_KEY;

// Lightweight Turnstile widget component
function TurnstileWidget({ onVerify, onExpire }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_ENABLED || !ref.current) return;
    if (!window.turnstile) {
      // Load Turnstile script once
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true;
      s.defer = true;
      s.onload = () => renderWidget();
      document.head.appendChild(s);
    } else {
      renderWidget();
    }
    function renderWidget() {
      if (!ref.current || ref.current.dataset.rendered) return;
      ref.current.dataset.rendered = '1';
      window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onVerify && onVerify(token),
        'expired-callback': () => onExpire && onExpire(),
        theme: 'light',
        size: 'normal',
      });
    }
    return () => {
      if (ref.current) delete ref.current.dataset.rendered;
    };
  }, []);
  if (!TURNSTILE_ENABLED) return null;
  return <div ref={ref} style={{ margin: '12px 0' }} />;
}

const SubscribeButton = ({ variant = 'default' }) => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [wallet, setWallet] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [walletSlots, setWalletSlots] = useState({ remaining: 100, total: 100 });

  // Password reset states
  const [showResetFields, setShowResetFields] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetCodeSent, setResetCodeSent] = useState(false);

  const navigate = useNavigate();

  // Check if user is authenticated
  const isAuthenticated = authService.isAuthenticated();
  const currentUser = authService.getCurrentUser();
  const isSuperAdmin = currentUser?.email === SUPER_ADMIN_EMAIL;

  useEffect(() => {
    if (showModal && step === 'register') {
      fetchWalletSlots();
    }
  }, [showModal, step]);

  const fetchWalletSlots = async () => {
    try {
      const data = await authService.getRemainingSlots();
      setWalletSlots({
        remaining: data.slots_remaining,
        total: data.total_slots
      });
    } catch (error) {
      console.error('Failed to fetch wallet slots:', error);
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const exists = await authService.checkEmail(email);

      if (exists) {
        setStep('login');
      } else {
        setStep('register');
      }
    } catch (error) {
      setMessage({ text: '❌ Error checking email', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const { user } = await authService.login(email, password);

      // Track login with Google Analytics
      trackLogin('email');
      setUserId(user.id);

      setMessage({
        text: `✅ Welcome back, ${user.email}!`,
        type: 'success'
      });

      setTimeout(() => {
        setShowModal(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      let errorMessage = 'Login failed';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setMessage({ text: `❌ ${errorMessage}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // Send reset code to email
      await authService.sendPasswordResetCode(email);

      // Track password reset request
      trackEvent('password_reset_requested', {
        email_domain: email.split('@')[1]
      });

      setResetCodeSent(true);
      setShowResetFields(true);
      setMessage({
        text: '✅ 6-digit code sent to your email (valid for 30 min)',
        type: 'success'
      });
    } catch (error) {
      let errorMessage = 'Failed to send reset code';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setMessage({ text: `❌ ${errorMessage}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // Verify code and reset password
      const { user } = await authService.resetPasswordWithCode(email, resetCode, newPassword);

      // Track successful password reset
      trackEvent('password_reset_completed');
      setUserId(user.id);

      setMessage({
        text: '✅ Password reset successful! Logging you in...',
        type: 'success'
      });

      setTimeout(() => {
        setShowModal(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      let errorMessage = 'Failed to reset password';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setMessage({ text: `❌ ${errorMessage}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const [turnstileToken, setTurnstileToken] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const { user } = await authService.register(email, password, wallet || null, turnstileToken);

      // Track signup with Google Analytics
      trackSignUp('email', !!wallet);
      setUserId(user.id);

      let messageText = '✅ Account created! Check your email to confirm.';
      if (wallet) {
        messageText += ' 💎 Wallet registered for rewards!';
      }

      setMessage({ text: messageText, type: 'success' });

      setTimeout(() => {
        setShowModal(false);
        window.location.href = window.location.href;
      }, 2000);
    } catch (error) {
      let errorMessage = 'Registration failed';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setMessage({ text: `❌ ${errorMessage}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep('email');
    setEmail('');
    setPassword('');
    setWallet('');
    setMessage({ text: '', type: '' });
    setShowResetFields(false);
    setResetCode('');
    setNewPassword('');
    setResetCodeSent(false);
  };

  const handleDashboardClick = () => {
    navigate('/admin/dashboard');
  };

  const getButtonClass = () => {
    switch (variant) {
      case 'header':
        return 'subscribe-btn subscribe-btn-header';
      case 'footer':
        return 'subscribe-btn subscribe-btn-footer';
      case 'floating':
        return 'subscribe-btn subscribe-btn-floating';
      default:
        return 'subscribe-btn subscribe-btn-default';
    }
  };

  // If user is logged in, don't show subscribe button
  if (isAuthenticated) {
    return null;
  }

  // Not logged in - show subscribe/login button
  return (
    <>
      <button
        className={getButtonClass()}
        onClick={() => setShowModal(true)}
        style={{ backgroundColor: '#667eea' }}
      >
        {variant === 'floating' ? '📧' : 'Subscribe / Login'}
      </button>

      {showModal && (
        <div className="subscribe-modal-overlay" onClick={() => { setShowModal(false); resetModal(); }}>
          <div className="subscribe-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setShowModal(false); resetModal(); }}>×</button>

            <div className="modal-content">

              {/* STEP 1: Email Entry */}
              {step === 'email' && (
                <>
                  <h2>📧 Join Focus-View</h2>
                  <p className="modal-cta">Enter your email to continue</p>

                  <form onSubmit={handleContinue} className="subscribe-form">
                    <input
                      type="email"
                      placeholder="Enter your email *"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="email-input"
                      disabled={loading}
                    />

                    <button
                      type="submit"
                      disabled={loading}
                      className="submit-btn"
                      style={{ backgroundColor: '#667eea' }}
                    >
                      {loading ? 'Checking...' : 'Continue →'}
                    </button>
                  </form>
                </>
              )}

              {/* STEP 2: Login (Existing User) */}
              {step === 'login' && (
                <>
                  <h2>👋 Welcome back!</h2>
                  <p className="modal-cta">Email: {email}</p>

                  <form onSubmit={showResetFields ? handleResetPassword : handleLogin} className="subscribe-form">
                    {!showResetFields ? (
                      <>
                        <input
                          type="password"
                          placeholder="Password *"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="email-input"
                          disabled={loading}
                          autoFocus
                        />

                        <button
                          type="submit"
                          disabled={loading}
                          className="submit-btn"
                          style={{ backgroundColor: '#667eea' }}
                        >
                          {loading ? 'Logging in...' : 'Login'}
                        </button>

                        {/* Forgot Password Link */}
                        <button
                          type="button"
                          onClick={handleForgotPassword}
                          className="text-btn"
                          style={{
                            marginTop: '10px',
                            background: 'none',
                            border: 'none',
                            color: '#667eea',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          disabled={loading}
                        >
                          Forgot Password?
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Password Reset Fields */}
                        <input
                          type="text"
                          placeholder="Enter 6-digit code from email *"
                          value={resetCode}
                          onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          required
                          className="email-input"
                          disabled={loading}
                          maxLength={6}
                          autoFocus
                          pattern="\d{6}"
                        />

                        <input
                          type="password"
                          placeholder="New password (min 8 characters) *"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          minLength={8}
                          className="email-input"
                          disabled={loading}
                          style={{ marginTop: '10px' }}
                        />

                        <button
                          type="submit"
                          disabled={loading || resetCode.length !== 6}
                          className="submit-btn"
                          style={{ backgroundColor: '#667eea' }}
                        >
                          {loading ? 'Resetting...' : 'Reset Password & Login'}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setShowResetFields(false);
                            setResetCode('');
                            setNewPassword('');
                            setMessage({ text: '', type: '' });
                          }}
                          className="submit-btn"
                          style={{ backgroundColor: '#999', marginTop: '10px' }}
                          disabled={loading}
                        >
                          Cancel Reset
                        </button>
                      </>
                    )}

                    {!showResetFields && (
                      <button
                        type="button"
                        onClick={resetModal}
                        className="submit-btn"
                        style={{ backgroundColor: '#999', marginTop: '10px' }}
                        disabled={loading}
                      >
                        ← Back
                      </button>
                    )}
                  </form>
                </>
              )}

              {/* STEP 3: Register (New User) */}
              {step === 'register' && (
                <>
                  <h2>🎉 Create Your Account</h2>
                  <p className="modal-cta">Email: {email}</p>

                  <form onSubmit={handleRegister} className="subscribe-form">
                    <input
                      type="password"
                      placeholder="Password (min 8 characters) *"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="email-input"
                      disabled={loading}
                      autoFocus
                    />

                    <p style={{
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '6px',
                      padding: '10px',
                      marginBottom: '10px',
                      fontSize: '13px',
                      color: '#856404',
                      textAlign: 'left'
                    }}>
                      ℹ️ <strong>Note:</strong> Earn loyalty rewards for every article you read.
                    </p>

                    <div className="wallet-field-container">
                      <input
                        type="text"
                        placeholder={
                          walletSlots.remaining > 0
                            ? "Redemption wallet address (optional)"
                            : "Wallet collection closed"
                        }
                        value={wallet}
                        onChange={(e) => setWallet(e.target.value)}
                        className="wallet-input"
                        disabled={loading || walletSlots.remaining === 0}
                        maxLength={34}
                      />
                      {walletSlots.remaining > 0 ? (
                        <p className="wallet-hint">
                          Wallet required only for reward redemptions. Earning rewards is free and requires no wallet.
                        </p>
                      ) : (
                        <p className="wallet-hint wallet-closed">
                          ⚠️ Wallet collection limit reached ({walletSlots.total}/{walletSlots.total})
                        </p>
                      )}
                    </div>

                    <TurnstileWidget
                      onVerify={(token) => setTurnstileToken(token)}
                      onExpire={() => setTurnstileToken('')}
                    />

                    <button
                      type="submit"
                      disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)}
                      className="submit-btn"
                      style={{ backgroundColor: '#667eea' }}
                    >
                      {loading ? 'Creating Account...' : 'Create Account'}
                    </button>

                    <button
                      type="button"
                      onClick={resetModal}
                      className="submit-btn"
                      style={{ backgroundColor: '#999', marginTop: '10px' }}
                      disabled={loading}
                    >
                      ← Back
                    </button>
                  </form>
                </>
              )}

              {message.text && (
                <p className={`subscribe-message ${message.type}`}>
                  {message.text}
                </p>
              )}

              <p className="privacy-text">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscribeButton;